import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { moeda } from '@/comuns/formato';
import {
    conteudoEmConsumo, custoDeConsumoCentavos, opcoesDeUnidade, quantidade,
    unidadeAbrev, unidadeLabel, unidadesCompativeis,
} from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import type { CategoriaDeProduto, Fornecedor, Marca, Produto } from '@/data/tipos';
import { centavos } from '@/screens/vendas/NovoOrcamentoModal';

export interface ProdutoModalProps {
    /** `null` fecha; um produto edita; `'novo'` cadastra. */
    alvo: Produto | 'novo' | null;
    categorias: CategoriaDeProduto[];
    marcas: Marca[];
    fornecedores: Fornecedor[];
    onClose: () => void;
    onSalvo: () => void;
}

interface Formulario {
    nome: string;
    categoriaId: string;
    marcaId: string;
    fornecedorId: string;
    lote: string;
    validade: string;
    quantidadeInicial: string;
    minimo: string;
    unidade: string;
    conteudo: string;
    unidadeConteudo: string;
    unidadeConsumo: string;
    custo: string;
}

const VAZIO: Formulario = {
    nome: '', categoriaId: '', marcaId: '', fornecedorId: '', lote: '', validade: '',
    quantidadeInicial: '0', minimo: '0', unidade: 'frasco', conteudo: '30',
    unidadeConteudo: 'ml', unidadeConsumo: 'ul', custo: '',
};

/**
 * Mantém o registro que já está lá mesmo desativado, marcado como tal: sem
 * isso, abrir um produto de marca inativa trocaria a marca dele em silêncio.
 */
function opcoesCom<T extends { id: string; nome: string }>(lista: T[], ativo: (item: T) => boolean, atual: string) {
    const ativos = lista.filter(ativo);
    const daVez = lista.find((x) => x.id === atual);
    const base = daVez && !ativo(daVez) ? [...ativos, daVez] : ativos;
    return base.map((x) => ({ value: x.id, label: ativo(x) ? x.nome : `${x.nome} (inativo)` }));
}

export function ProdutoModal({ alvo, categorias, marcas, fornecedores, onClose, onSalvo }: ProdutoModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idMarca = useId();
    const idFornecedor = useId();
    const idCategoria = useId();
    const idUnidade = useId();
    const idConteudo = useId();
    const idUnidadeConteudo = useId();
    const idUnidadeConsumo = useId();
    const idLote = useId();
    const idValidade = useId();
    const idQuantidade = useId();
    const idMinimo = useId();
    const idCusto = useId();

    const [form, setForm] = useState<Formulario>(VAZIO);
    const formSujo = useAlteracaoPendente(form, alvo);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const novo = alvo === 'novo';

    useEffect(() => {
        if (!alvo) return;
        setErro(null);
        if (alvo === 'novo') {
            setForm({
                ...VAZIO,
                categoriaId: categorias.find((c) => c.ativa)?.id ?? '',
                marcaId: marcas.find((m) => m.ativa)?.id ?? '',
                fornecedorId: fornecedores.find((f) => f.ativo)?.id ?? '',
            });
            return;
        }
        setForm({
            nome: alvo.nome,
            categoriaId: alvo.categoriaId ?? '',
            marcaId: alvo.marcaId ?? '',
            fornecedorId: alvo.fornecedorId ?? '',
            lote: alvo.lote,
            validade: alvo.validade ?? '',
            quantidadeInicial: String(alvo.quantidade),
            minimo: String(alvo.minimo),
            unidade: alvo.unidade,
            conteudo: String(alvo.conteudo),
            unidadeConteudo: alvo.unidadeConteudo,
            unidadeConsumo: alvo.unidadeConsumo,
            custo: (alvo.custoCentavos / 100).toFixed(2).replace('.', ','),
        });
    }, [alvo, categorias, marcas, fornecedores]);

    if (!alvo) return null;

    const campo = (chave: keyof Formulario) => (valor: string) => {
        setForm((f) => ({ ...f, [chave]: valor }));
        setErro(null);
    };

    const medida = {
        unidade: form.unidade,
        conteudo: Number(form.conteudo) || 0,
        unidadeConteudo: form.unidadeConteudo,
        unidadeConsumo: form.unidadeConsumo,
    };
    const porEmbalagem = conteudoEmConsumo(medida);
    const custoCentavos = centavos(form.custo);

    const salvar = async () => {
        setSalvando(true);
        try {
            const comuns = {
                nome: form.nome,
                categoriaId: form.categoriaId || undefined,
                marcaId: form.marcaId || undefined,
                fornecedorId: form.fornecedorId || undefined,
                lote: form.lote,
                validade: form.validade || undefined,
                minimo: Number(form.minimo) || 0,
                unidade: form.unidade,
                conteudo: Number(form.conteudo) || 0,
                unidadeConteudo: form.unidadeConteudo,
                unidadeConsumo: form.unidadeConsumo,
                custoCentavos,
            };
            if (novo) {
                await dados().criarProduto({ ...comuns, quantidadeInicial: Number(form.quantidadeInicial) || 0 });
                aviso({ title: 'Produto cadastrado', description: `${form.nome} · disponível no estoque` });
            } else {
                await dados().atualizarProduto(alvo.id, comuns);
                aviso({ title: 'Produto atualizado', description: form.nome });
            }
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={620}
            title={novo ? 'Novo produto' : 'Editar produto'}
            description={novo
                ? 'Cadastre um insumo com marca, fornecedor, lote e nível mínimo.'
                : 'Alterar unidade ou conteúdo recalcula o custo por consumo dos procedimentos.'}
            dirty={formSujo}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!form.nome.trim()}>
                        {novo ? 'Cadastrar' : 'Salvar'}
                    </Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField label="Nome do produto" required htmlFor={idNome} style={{ gridColumn: '1 / -1' }}>
                        <Input id={idNome} value={form.nome} onChange={campo('nome')} placeholder="Ex.: Toxina botulínica 100 UI" />
                    </FormField>

                    <FormField label="Marca" htmlFor={idMarca} hint="Cadastradas na aba Marcas">
                        <Select id={idMarca} value={form.marcaId} onChange={campo('marcaId')} options={opcoesCom(marcas, (m) => m.ativa, form.marcaId)} />
                    </FormField>
                    <FormField label="Fornecedor" htmlFor={idFornecedor} hint="Cadastrados na aba Fornecedores">
                        <Select id={idFornecedor} value={form.fornecedorId} onChange={campo('fornecedorId')} options={opcoesCom(fornecedores, (f) => f.ativo, form.fornecedorId)} />
                    </FormField>
                    <FormField label="Categoria" htmlFor={idCategoria} style={{ gridColumn: '1 / -1' }} hint="Cadastradas na aba Categorias de produto">
                        <Select id={idCategoria} value={form.categoriaId} onChange={campo('categoriaId')} options={opcoesCom(categorias, (c) => c.ativa, form.categoriaId)} />
                    </FormField>

                    {/* Duas unidades porque são duas perguntas: em que o estoque é
                        CONTADO e em que o procedimento DEBITA. Um frasco de 100 UI
                        entra como 1 frasco e sai em UI. */}
                    <FormField label="Unidade de medida" required htmlFor={idUnidade} hint="Como o estoque é contado e comprado">
                        <Select id={idUnidade} value={form.unidade} onChange={campo('unidade')} options={opcoesDeUnidade()} />
                    </FormField>
                    <FormField label="Quantidade por embalagem" required htmlFor={idConteudo} hint={`Quanto vem em cada ${unidadeAbrev(form.unidade)}`}>
                        <span style={{ display: 'flex', gap: 8 }}>
                            <Input id={idConteudo} type="number" value={form.conteudo} onChange={campo('conteudo')} style={{ flex: 1 }} />
                            <span style={{ width: 128, flex: '0 0 auto' }}>
                                <Select
                                    id={idUnidadeConteudo} value={form.unidadeConteudo}
                                    onChange={(v) => setForm((f) => ({
                                        ...f,
                                        unidadeConteudo: v,
                                        /* Trocar a dimensão do conteúdo invalida a unidade de
                                           consumo: debitar em µl um conteúdo em gramas não
                                           converte, e o custo sairia por um fator de mil. */
                                        unidadeConsumo: unidadesCompativeis(v).includes(f.unidadeConsumo) ? f.unidadeConsumo : v,
                                    }))}
                                    options={opcoesDeUnidade()}
                                />
                            </span>
                        </span>
                    </FormField>

                    <FormField
                        label="Unidade de consumo" required htmlFor={idUnidadeConsumo} style={{ gridColumn: '1 / -1' }}
                        hint={porEmbalagem > 0
                            ? `1 ${unidadeAbrev(form.unidade)} de ${quantidade(Number(form.conteudo) || 0)} ${unidadeAbrev(form.unidadeConteudo)} = ${quantidade(porEmbalagem)} ${unidadeAbrev(form.unidadeConsumo)} para debitar nos procedimentos${custoCentavos > 0 ? ` · ${moeda(custoDeConsumoCentavos({ ...medida, custoCentavos }))}/${unidadeAbrev(form.unidadeConsumo)}` : ''}`
                            : 'Informe a quantidade por embalagem para calcular o consumo'}
                    >
                        <Select
                            id={idUnidadeConsumo} value={form.unidadeConsumo} onChange={campo('unidadeConsumo')}
                            options={unidadesCompativeis(form.unidadeConteudo).map((u) => ({ value: u, label: unidadeLabel(1, u) }))}
                        />
                    </FormField>

                    <FormField label="Lote" htmlFor={idLote}>
                        <Input id={idLote} value={form.lote} onChange={campo('lote')} placeholder="BT-2291" />
                    </FormField>
                    <FormField label="Validade" htmlFor={idValidade} hint="O mês impresso no rótulo">
                        <Input id={idValidade} type="date" value={form.validade} onChange={campo('validade')} />
                    </FormField>

                    {/* Saldo só na criação: depois disso ele é consequência das
                        movimentações, e digitar por cima apagaria a história de
                        onde o estoque foi parar. */}
                    {novo ? (
                        <FormField label="Quantidade inicial" htmlFor={idQuantidade} hint={`Em ${unidadeLabel(2, form.unidade)} — entra como movimentação de saldo inicial`}>
                            <Input id={idQuantidade} type="number" value={form.quantidadeInicial} onChange={campo('quantidadeInicial')} />
                        </FormField>
                    ) : (
                        <FormField label="Quantidade em estoque" htmlFor={idQuantidade} hint="Muda por entrada ou saída, não por edição">
                            <Input id={idQuantidade} value={`${quantidade(alvo.quantidade)} ${unidadeLabel(alvo.quantidade, alvo.unidade)}`} onChange={() => {}} disabled />
                        </FormField>
                    )}
                    <FormField label="Nível mínimo" htmlFor={idMinimo} hint="Dispara o alerta de reposição">
                        <Input id={idMinimo} type="number" value={form.minimo} onChange={campo('minimo')} />
                    </FormField>
                    <FormField
                        label="Custo unitário" htmlFor={idCusto} style={{ gridColumn: '1 / -1' }}
                        hint={`Por ${unidadeAbrev(form.unidade)} — o custo por ${unidadeAbrev(form.unidadeConsumo)} é derivado dele`}
                    >
                        <Input id={idCusto} value={form.custo} onChange={campo('custo')} placeholder="0,00" />
                    </FormField>
                </div>
            </div>
        </Modal>
    );
}
